import {
  Box,
  RadioGroup as ChakraRadioGroup,
  AspectRatio,
  type RadioGroupRootProps,
  type HTMLChakraProps
} from "@chakra-ui/react";

import { asSlotComponent } from "../internal/v3-slot";
import { Image } from "../image/image";

const RadioGroupItem = asSlotComponent(ChakraRadioGroup.Item);
const RadioGroupItemText = asSlotComponent(ChakraRadioGroup.ItemText);
const RadioGroupItemHiddenInput = asSlotComponent(
  ChakraRadioGroup.ItemHiddenInput
);
const RadioGroupItemIndicator = asSlotComponent(ChakraRadioGroup.ItemIndicator);

export type RadioProps = HTMLChakraProps<"div"> & {
  value: string;
  size?: string | Record<string, string> | Array<string | null>;
  imageSrc?: string;
  imageAlt?: string;
  children?: React.ReactNode;
  disabled?: boolean;
  name?: string;
};

function Radio({ imageSrc, imageAlt, children, value, ...rest }: RadioProps) {
  const radio = (
    <RadioGroupItem value={value} {...rest}>
      <RadioGroupItemHiddenInput />
      <RadioGroupItemIndicator />
      {children ? <RadioGroupItemText>{children}</RadioGroupItemText> : null}
    </RadioGroupItem>
  );

  if (imageSrc) {
    return (
      <Box maxW="36rem">
        <AspectRatio ratio={16 / 9} mb={2}>
          <Image
            src={imageSrc}
            alt={imageAlt}
            aria-hidden="true"
            magnify={true}
            h="100%"
            w="100%"
          />
        </AspectRatio>
        {radio}
      </Box>
    );
  }

  return <Box maxW="36rem">{radio}</Box>;
}

function RadioGroup({
  colorPalette = "blue",
  size,
  ...props
}: RadioGroupRootProps & {
  size?: string | Record<string, string> | Array<string | null>;
}) {
  return (
    <ChakraRadioGroup.Root
      colorPalette={colorPalette}
      size={size as RadioGroupRootProps["size"]}
      {...props}
    />
  );
}

export { Radio, RadioGroup };
